/**
 * Inventory Layout Component
 * 
 * Provides consistent sidebar navigation across all inventory module pages.
 * Includes collapsible sidebar with module navigation and AS9100D compliance info.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Package,
  MapPin,
  ClipboardList,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  Shield,
  Sliders,
  Boxes,
  PanelLeftClose,
  PanelLeft,
  FileWarning
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/**
 * Inventory Modules Configuration
 */
const INVENTORY_MODULES = [
  { title: 'Dashboard', icon: Boxes, path: '/inventory', exact: true },
  { title: 'Locations', icon: MapPin, path: '/inventory/locations' },
  { title: 'Stock', icon: Package, path: '/inventory/stock' },
  { title: 'Lots', icon: Boxes, path: '/inventory/lots' },
  { title: 'Receiving', icon: ArrowDownToLine, path: '/inventory/receiving' },
  { title: 'Issue/Transfer', icon: ArrowUpFromLine, path: '/inventory/issue-transfer' },
  { title: 'Cycle Count', icon: ClipboardList, path: '/inventory/cycle-counts' },
  { title: 'Quarantine', icon: AlertTriangle, path: '/inventory/quarantine' },
  { title: 'NCR', icon: FileWarning, path: '/inventory/ncr' },
  { title: 'Adjustments', icon: Sliders, path: '/inventory/adjustments' },
  { title: 'QC Dashboard', icon: Shield, path: '/inventory/qc-dashboard' },
];

/**
 * Sidebar Item Component
 */
const SidebarItem = ({ module, isActive, isCollapsed, onClick }) => {
  const Icon = module.icon;
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={isCollapsed ? 100 : 1000}>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
              isActive 
                ? 'bg-[#101957] text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-[#101957]'
            } ${isCollapsed ? 'justify-center' : ''}`}
            data-testid={`sidebar-${module.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : ''}`} />
            {!isCollapsed && (
              <>
                <span className="text-sm font-medium flex-1 text-left">{module.title}</span>
                {module.badge && (
                  <Badge 
                    variant={isActive ? "secondary" : "outline"} 
                    className={`text-xs ${isActive ? 'bg-white/20 text-white' : ''}`}
                  >
                    {module.badge}
                  </Badge>
                )}
              </>
            )}
          </button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right">
            <p>{module.title}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Inventory Sidebar Component
 */
const InventorySidebar = ({ isCollapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div 
      className={`flex-shrink-0 bg-white border-r border-gray-200 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-56'
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Sidebar Header */}
        <div className={`p-4 border-b border-gray-100 ${isCollapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#101957] flex items-center justify-center">
                  <Boxes className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-[#101957] text-sm">Inventory</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggle}
              data-testid="sidebar-toggle"
            >
              {isCollapsed ? (
                <PanelLeft className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
        
        {/* Module Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {INVENTORY_MODULES.map((module) => {
            const isActive = module.exact 
              ? location.pathname === module.path
              : location.pathname.startsWith(module.path) && module.path !== '/inventory';
            
            return (
              <SidebarItem
                key={module.path}
                module={module}
                isActive={isActive}
                isCollapsed={isCollapsed}
                onClick={() => navigate(module.path)}
              />
            );
          })}
        </nav>
        
        {/* Sidebar Footer - Compliance Badge */}
        {!isCollapsed && (
          <div className="p-3 border-t border-gray-100">
            <div className="p-3 bg-[#101957]/5 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-[#101957]" />
                <span className="text-xs font-semibold text-[#101957]">AS9100D</span>
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                Compliant inventory management system
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Inventory Layout Component
 * Wraps page content with consistent sidebar navigation
 * 
 * @param {React.ReactNode} children - Page content to render
 * @param {string} className - Additional classes for the content area
 */
const InventoryLayout = ({ children, className = '' }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="h-full flex bg-slate-50">
      {/* Left Sidebar */}
      <InventorySidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />

      {/* Main Content Area */}
      <div className={`flex-1 overflow-auto ${className}`}>
        {children}
      </div>
    </div>
  );
};

export default InventoryLayout;
export { InventorySidebar, INVENTORY_MODULES };
